/**
 * Initials for the v2 Avatar (redesign phase 5, #455). At implementation this
 * moves to `lib/` and replaces the web app's `getInitials(Person)`, which
 * dropped every initial outside [a-zA-Z] (Ö, Ü, Ä).
 *
 * Up to two letters: the first letter of the first and of the last part, so
 * "Anna-Lena von Weiß" is "AW" and a single name one letter. Letters are any
 * Unicode letter or digit, upper-cased in German. An email is reduced to the
 * part before "@" and split on "." / "_" / "-" ("max.müller@…" → "MM").
 * Nothing usable gives "?".
 */

export type InitialsKind = "person" | "organisation";

/** Leading titles that aren't part of the name. */
const TITLES = new Set(["dr", "prof", "dipl", "ing", "herr", "frau"]);

/** Legal forms: "Müller GmbH & Co. KG" is "M", "Bosch Rexroth AG" is "BR". */
const LEGAL_FORMS = new Set(["gmbh", "mbh", "ag", "kg", "kgaa", "se", "ug", "ohg", "gbr", "ek", "ev", "co", "haftungsbeschränkt"]);

const LETTER = /[\p{L}\p{N}]/u;

function normalise(part: string): string {
  return part.toLocaleLowerCase("de-DE").replace(/[^\p{L}\p{N}]/gu, "");
}

function initialOf(part: string): string {
  const letter = part.match(LETTER)?.[0] ?? "";
  // "ß" upper-cases to "SS"; one character is the initial.
  return [...letter.toLocaleUpperCase("de-DE")][0] ?? "";
}

export function getInitials(name: string | null | undefined, { kind = "person" }: { kind?: InitialsKind } = {}): string {
  let text = (name ?? "").trim();
  if (kind === "person" && text.includes("@")) text = text.split("@")[0];

  const stop = kind === "organisation" ? LEGAL_FORMS : TITLES;
  // People split on "." too (email local parts); organisations don't ("Co.", "e.K.").
  const parts = text
    .split(kind === "organisation" ? /[\s/]+/ : /[\s._/-]+/)
    .filter((part) => LETTER.test(part) && !stop.has(normalise(part)));

  if (parts.length === 0) return "?";
  if (parts.length === 1) return initialOf(parts[0]);
  return initialOf(parts[0]) + initialOf(parts[parts.length - 1]);
}
